import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';

import { shuffleArray } from 'utils';

import QuestionHeader from './QuestionHeader';
import QuestionPassage from './QuestionPassage';
import QuestionMedia from './QuestionMedia';
import QuestionOptions from './QuestionOptions';
import QuestionWriting from './QuestionWriting';
import QuestionSpeaking from './QuestionSpeaking';

/**
 * @component SectionForm
 * @description
 * Componente principal para mostrar y gestionar una sección de preguntas del examen.
 * Dependiendo del `sectionId`, muestra diferentes tipos de interacción:
 * - Opción múltiple
 * - Respuesta escrita (Writing)
 * - Grabación de audio (Speaking)
 *
 * También expone al padre una función `stopRecordingExternally` para detener la grabación desde el exterior
 * durante la sección de Speaking (sección 10).
 *
 * Utiliza subcomponentes para mantener cada parte desacoplada según el principio KISS.
 *
 * @param {Object} props
 * @param {Object} props.currentQuestion - Objeto con datos de la pregunta actual
 * @param {Function} props.onSelectAnswer - Función callback para propagar la respuesta seleccionada al padre
 * @param {number} props.sectionId - Número de sección actual (determina tipo de interacción)
 *
 * @returns {JSX.Element}
 *
 * @example
 * <SectionForm
 *   ref={formRef}
 *   currentQuestion={question}
 *   sectionId={8}
 *   onSelectAnswer={(answer) => console.log(answer)}
 * />
 */
const SectionForm = forwardRef(({ currentQuestion, onSelectAnswer, sectionId }, ref) => {
	const [answers, setAnswers] = useState([]);
	const [selectedAnswer, setSelectedAnswer] = useState(null);
	const audioRef = useRef();

	/**
	 * Expone al padre una función para detener la grabación de audio desde el exterior
	 */
	useImperativeHandle(ref, () => ({
		stopRecordingExternally: () =>
			sectionId === 10 && audioRef.current?.stopRecordingExternally
				? audioRef.current.stopRecordingExternally()
				: Promise.resolve(null)
	}));

	/**
	 * Carga nuevas respuestas cuando cambia la pregunta
	 * y reinicia la selección
	 */
	useEffect(() => {
		if (Array.isArray(currentQuestion?.answers)) {
			setSelectedAnswer(null);
			setAnswers(shuffleArray(currentQuestion.answers));
		}
	}, [currentQuestion]);

	/**
	 * Asigna y propaga la respuesta seleccionada
	 */
	const setAnswer = (value) => {
		setSelectedAnswer(value);
		onSelectAnswer(value);
	};

	return (
		<>
			<QuestionHeader
				instruction={currentQuestion.question_instruction}
				question={currentQuestion.question}
			/>
			<QuestionPassage passage={currentQuestion.passage} />
			<QuestionMedia image={currentQuestion.image} audio={currentQuestion.audio} />

			{![8, 10].includes(sectionId) && (
				<QuestionOptions
					answers={answers}
					selected={selectedAnswer}
					onChange={setAnswer}
					questionId={currentQuestion.question_id}
				/>
			)}

			{sectionId === 8 && (
				<QuestionWriting value={selectedAnswer?.value} onChange={setAnswer} />
			)}

			{sectionId === 10 && (
				<QuestionSpeaking
					audioRef={audioRef}
					onAudioComplete={(file) => setAnswer({ type: 'audio', file })}
				/>
			)}
		</>
	);
});

export default SectionForm;